// Right-side Products panel: fetch the anon designer catalog and render the waterfall product picker.

import { eventBus, EVENTS } from '../../core/event-bus.js'
import { createLogger } from '../../utils/logger.js'
import { CONFIG, getCdnThumbnail } from '../../core/config.js'
import { getSessionId } from '../../api/index.js'
import { observeImpressions, trackRecClick } from '../../utils/tracking-funnel.js'

const log = createLogger('design')

// Design panel population state
const panelState = {
  galleryLoaded: false,
  productsLoaded: false,
  productsData: [],
  productsOffset: 0,
  productsHasMore: true,
  productsLoading: false,
}

/**
 * Populate the right Products panel with product cards
 */
export async function populateDesignProducts() {
  const grid = document.getElementById('designProductsGrid')
  if (!grid || panelState.productsLoading) return

  if (panelState.productsLoaded && panelState.productsData.length > 0) return

  panelState.productsLoading = true
  grid.innerHTML = `
    <div class="waterfall-container">
      <div class="waterfall-column">
        ${Array(3).fill('<div class="skeleton-product"><div class="skeleton-image"></div><div class="skeleton-text"></div></div>').join('')}
      </div>
      <div class="waterfall-column">
        ${Array(3).fill('<div class="skeleton-product"><div class="skeleton-image"></div><div class="skeleton-text"></div></div>').join('')}
      </div>
    </div>`

  try {
    const sessionId = getSessionId()
    // creator-api anon catalog lives at /api/customer/base-products
    // (ADR-006; the bare /api/base-products path 404s).
    const url = `${CONFIG.FRONTEND_API}/api/customer/base-products?scene=designer&limit=20&offset=0&session_id=${sessionId}`
    const res = await fetch(url)
    const result = await res.json()

    if (!result.success || !result.products?.length) {
      grid.innerHTML = '<div class="placeholder-text">No products available</div>'
      panelState.productsLoading = false
      return
    }

    panelState.productsData = result.products
    panelState.productsLoaded = true
    grid.innerHTML = ''

    const container = document.createElement('div')
    container.className = 'waterfall-container'
    const col0 = document.createElement('div')
    col0.className = 'waterfall-column'
    const col1 = document.createElement('div')
    col1.className = 'waterfall-column'
    container.appendChild(col0)
    container.appendChild(col1)

    const heights = [0, 0]
    result.products.forEach((product, idx) => {
      // Base-products carry a real thumbnail URL (cdn.sdspod.com) and a ULID
      // id — getCdnThumbnail would build a nonexistent CF-delivery URL from
      // that ULID (every image 404ed, the tab looked empty).
      const imageUrl = product.thumbnail || product.thumbnail_url || getCdnThumbnail(product)
      const sdsId = product.sds_id
      const name = product.sds_par_english_name || product.name || 'Product' // legacy-ref-ok: v3.2 rename pending

      const item = document.createElement('div')
      // .rec-card flags this for the recommendation funnel observer (H-002).
      item.className = 'product-item rec-card'
      item.dataset.productId = String(sdsId)
      item.dataset.position = String(idx)

      const img = document.createElement('img')
      img.src = imageUrl
      img.loading = idx < 8 ? 'eager' : 'lazy'
      img.alt = name
      item.appendChild(img)

      if (product.reason) {
        const badge = document.createElement('div')
        badge.className = 'rec-badge'
        const labels = { personalized: 'FOR YOU', trending: 'TRENDING', popular: 'POPULAR', similar: 'SIMILAR', explore: 'NEW' }
        badge.textContent = labels[product.reason] || 'REC'
        item.appendChild(badge)
      }

      item.addEventListener('click', () => {
        // Highlight active
        grid.querySelectorAll('.product-item').forEach(el => el.classList.remove('active'))
        item.classList.add('active')

        // Recommendation funnel: click event uses the recommendationId
        // stamped onto the card by observeImpressions when the card first
        // entered the viewport.
        if (item.dataset.recId) trackRecClick(item.dataset.recId)

        // Emit product select
        eventBus.emit(EVENTS.PRODUCT_SELECT, {
          id: sdsId,
          name,
          price: product.current_price || product.price || product.mms_back_end_price_usd, // legacy-ref-ok: v3.2 rename pending
          variants: product.variants,
          thumbnail: imageUrl,
          // SDS production material-size requirement — needed by add-to-design's
          // picture_request gate (sds-data-model.md §6).
          picture_request: product.picture_request,
          recommendationId: item.dataset.recId || null,
        })
      })

      const shortest = heights[0] <= heights[1] ? 0 : 1
      ;(shortest === 0 ? col0 : col1).appendChild(item)
      heights[shortest] += 200 + 12
    })

    grid.appendChild(container)
    log.info(`Design products panel: loaded ${result.products.length} products`)

    // Wire IntersectionObserver for impression events on the design product
    // picker (scenario=designer_style_picks). Each card mints a
    // recommendationId when it crosses the 50% visible threshold; the click
    // handler above reuses that id so impression and click join in
    // recommendation_funnel.
    observeImpressions({
      selector: '.rec-card',
      scenario: 'designer_style_picks',
      pageType: 'designer',
      root: grid,
      getSessionId,
    })
  } catch (err) {
    log.error('Failed to load design products:', err)
    grid.innerHTML = '<div class="placeholder-text">Failed to load products</div>'
  } finally {
    panelState.productsLoading = false
  }
}
